import { useEffect, useRef } from "react";
import { useLocation } from "wouter";

const DEFAULT_MESSAGE = "You have unsaved changes that may be lost.";

export function useUnloadWarning(
  hasUnsavedChanges: boolean,
  message: string = DEFAULT_MESSAGE,
): void {
  const dirtyRef = useRef(hasUnsavedChanges);
  dirtyRef.current = hasUnsavedChanges;

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = message;
      return message;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsavedChanges, message]);

  const [, navigate] = useLocation();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const confirmIfDirty = (): boolean => {
      if (!dirtyRef.current) return true;
      return window.confirm(message);
    };

    const onPopState = (event: PopStateEvent) => {
      if (!dirtyRef.current) return;
      if (!window.confirm(message)) {
        history.pushState(null, "", window.location.href);
        event.preventDefault?.();
      }
    };

    const onClickCapture = (event: MouseEvent) => {
      if (!dirtyRef.current) return;
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest?.("a") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      if (anchor.target && anchor.target !== "" && anchor.target !== "_self") return;
      if (anchor.hasAttribute("download")) return;
      if (href.startsWith("mailto:") || href.startsWith("tel:")) return;
      if (!confirmIfDirty()) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener("popstate", onPopState);
    document.addEventListener("click", onClickCapture, true);
    return () => {
      window.removeEventListener("popstate", onPopState);
      document.removeEventListener("click", onClickCapture, true);
    };
  }, [message]);
}

export default useUnloadWarning;
